import { TFunction } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import { CONTROL_ACTIVITIES_LIST_COLUMN_IDS as COLUMN_ID } from "@impero-com/ui/domain";
import { Row } from "@tanstack/react-table";

import { ControlActivityTableRow } from "../types";

interface LatestControlTitle {
    created: number;
    title: string;
}

interface LatestControlTitleOption {
    controlId: string;
    title: string;
}

/**
 * Builds the control filter options, one per controlId.
 *
 * When a control is renamed the same controlId appears with several results (different titles) in the table.
 * We only want to show the latest renamed title from the latest sent out control result (the one with the latest created date).
 */
export function buildLatestControlTitleOptions(
    rows: readonly Row<ControlActivityTableRow>[]
): LatestControlTitleOption[] {
    const latestTitles = new Map<string, LatestControlTitle>();
    for (const { original } of rows) {
        const { controlId, title, created } = original;
        const createdTime = new Date(created).getTime();
        const existing = latestTitles.get(controlId);
        if (!existing || createdTime > existing.created) {
            latestTitles.set(controlId, { created: createdTime, title });
        }
    }
    return latestTitles
        .entries()
        .map(([controlId, { title }]) => ({
            controlId,
            title,
        }))
        .toArray();
}

/**
 * The person/user columns whose filter selects share the same localized
 * "Select …" placeholder pattern.
 */
type UserFilterColumnId =
    | typeof COLUMN_ID.RESPONSIBLE
    | typeof COLUMN_ID.CLAIMANT
    | typeof COLUMN_ID.PERFORMER
    | typeof COLUMN_ID.REVIEWER
    | typeof COLUMN_ID.REVIEW_PERFORMER
    | typeof COLUMN_ID.SECOND_REVIEWER
    | typeof COLUMN_ID.SECOND_REVIEW_PERFORMER;

export function localizeColumnFilterPlaceholder(
    columnId: UserFilterColumnId,
    t: TFunction
): string {
    switch (columnId) {
        case COLUMN_ID.RESPONSIBLE:
            return t("ControlActivitiesList_ResponsibleFilter_Placeholder");
        case COLUMN_ID.CLAIMANT:
            return t("ControlActivitiesList_ClaimantFilter_Placeholder");
        case COLUMN_ID.PERFORMER:
            return t("ControlActivitiesList_PerformerFilter_Placeholder");
        case COLUMN_ID.REVIEWER:
            return t("ControlActivitiesList_ReviewerFilter_Placeholder");
        case COLUMN_ID.REVIEW_PERFORMER:
            return t("ControlActivitiesList_ReviewPerformerFilter_Placeholder");
        case COLUMN_ID.SECOND_REVIEWER:
            return t("ControlActivitiesList_SecondReviewerFilter_Placeholder");
        case COLUMN_ID.SECOND_REVIEW_PERFORMER:
            return t(
                "ControlActivitiesList_SecondReviewPerformerFilter_Placeholder"
            );
        default:
            return assertNever(columnId);
    }
}
